import type { Outcome } from "@landing/types"
import { useCallback, useRef, useState } from "react"
import { playBracketClickSound, playBracketDeselectSound, playBracketErrorSound } from "../../landing/LandingSounds"

interface UseOutcomeInteractionProps {
  outcome: Outcome
  heldOutcomes: string[]
  selectedOutcomes: string[]
  onOutcomeClick: (outcomeName: string) => void
  isClickable: boolean
}

export function useOutcomeInteraction({
  outcome,
  heldOutcomes,
  selectedOutcomes,
  onOutcomeClick,
  isClickable,
}: UseOutcomeInteractionProps) {
  const hasInteractedRef = useRef(false)
  const [isPressed, setIsPressed] = useState(false)

  const handlePointerDown = useCallback(() => {
    if (!isClickable) return
    setIsPressed(true)
  }, [isClickable])

  const handlePointerUp = useCallback(() => {
    if (!(isClickable && isPressed)) return

    if (hasInteractedRef.current) return

    hasInteractedRef.current = true
    setIsPressed(false)

    if (outcome.state === "eliminated" && !heldOutcomes.includes(outcome.slug)) {
      playBracketErrorSound()
      setTimeout(() => {
        hasInteractedRef.current = false
      }, 25)
      return
    }

    if (selectedOutcomes.includes(outcome.name)) {
      playBracketDeselectSound()
      onOutcomeClick(outcome.name)
      setTimeout(() => {
        hasInteractedRef.current = false
      }, 25)
      return
    }

    if (selectedOutcomes.length >= 5) {
      playBracketErrorSound()
      setTimeout(() => {
        hasInteractedRef.current = false
      }, 25)
      return
    }

    playBracketClickSound(selectedOutcomes.length + 1)
    onOutcomeClick(outcome.name)

    setTimeout(() => {
      hasInteractedRef.current = false
    }, 25)
  }, [isClickable, isPressed, outcome, heldOutcomes, selectedOutcomes, onOutcomeClick])

  const handlePointerLeave = useCallback(() => {
    setIsPressed(false)
    hasInteractedRef.current = false
  }, [])

  const handlePointerCancel = useCallback(() => {
    setIsPressed(false)
    hasInteractedRef.current = false
  }, [])

  return {
    isPressed,
    handlers: {
      onPointerDown: handlePointerDown,
      onPointerUp: handlePointerUp,
      onPointerLeave: handlePointerLeave,
      onPointerCancel: handlePointerCancel,
    },
  }
}
